import { RightOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, Col, Input, List, Row, Typography, theme, Popover } from "antd";
import React, { useState, useMemo, useEffect } from "react";
import styled from 'styled-components';
import SubmenuGroups from './SubmenuGroups';
import { getPrecursorGroups, getPrecursorGroupsByCategory } from '../api/chemicalRegistry';
import { useParams } from 'react-router-dom';

const { useToken } = theme;

interface StyledListItemProps {
  $isActive: boolean;
  $blueColor: string;
}

const StyledListItem = styled(List.Item)<StyledListItemProps>`
  && {
    background-color: ${props => props.$isActive ? "#e6f7ff" : "transparent"};
    cursor: pointer;
    padding: 8px 12px 8px 15px;
    border-radius: 4px;
    margin-bottom: 4px;
    transition: all 0.3s;
    color: ${props => props.$isActive ? props.$blueColor : 'rgba(0, 0, 0, 0.85)'};
    font-weight: ${props => props.$isActive ? 500 : 400};
    border-radius: 4px !important;
    border-bottom: none;

    &:hover {
      background-color: ${props => props.$isActive ? "#e6f7ff" : "#f5f5f5"};
    }
  }
`;

interface PrecursorGroupsProps {
  onGroupSelect?: (group: string) => void;
  selectedGroup?: string;
}

const PrecursorGroups: React.FC<PrecursorGroupsProps> = ({ onGroupSelect, selectedGroup }) => {
  const { token } = useToken();
  const { laboratoryId } = useParams<{ laboratoryId: string }>();
  const [precursorGroups, setPrecursorGroups] = useState<string[]>([]);
  const [activeItem, setActiveItem] = useState<string>(selectedGroup || '');
  const [selectedCategory, setSelectedCategory] = useState<string>(
    selectedGroup && selectedGroup.startsWith('Группа ') ? selectedGroup : ''
  );
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🔍 PrecursorGroups: selectedCategory changed to:', selectedCategory);
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedGroup !== activeItem) {
      console.log('🔄 PrecursorGroups: syncing with parent selectedGroup:', selectedGroup);
      setActiveItem(selectedGroup || '');
      setSelectedCategory(selectedGroup && selectedGroup.startsWith('Группа ') ? selectedGroup : '');
    }
  }, [selectedGroup, activeItem]);

  useEffect(() => {
    const fetchPrecursorGroups = async () => {
      if (!laboratoryId) return;
      
      try {
        setLoading(true);
        console.log('🔄 PrecursorGroups: fetchPrecursorGroups triggered, laboratoryId:', laboratoryId, 'selectedCategory:', selectedCategory);
        
        let groups;
        if (selectedCategory && selectedCategory.startsWith('Группа ')) {
          console.log('📡 PrecursorGroups: fetching groups by category:', selectedCategory);
          groups = await getPrecursorGroupsByCategory(laboratoryId, selectedCategory);
          console.log('📡 PrecursorGroups: received groups by category:', groups);
        } else {
          console.log('📡 PrecursorGroups: fetching all groups');
          groups = await getPrecursorGroups(laboratoryId);
          console.log('📡 PrecursorGroups: received all groups:', groups);
        }
        
        console.log('✅ PrecursorGroups: fetched', groups.length, 'groups for category:', selectedCategory);
        setPrecursorGroups(groups);
      } catch (error) {
        console.error('❌ PrecursorGroups: Error fetching precursor groups:', error);
      } finally {
        setLoading(false);
      }
    };

    console.log('🚀 PrecursorGroups: useEffect triggered with laboratoryId:', laboratoryId, 'selectedCategory:', selectedCategory);
    fetchPrecursorGroups();
  }, [laboratoryId, selectedCategory]);

  const filteredData = useMemo(() => {
    return precursorGroups.filter(item => 
      item.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [precursorGroups, searchText]);

  const handleItemClick = (item: string) => {
    console.log('🎲 PrecursorGroups: selecting precursor group:', item, 'current active:', activeItem);
    if (item !== activeItem) {
      console.log('✅ PrecursorGroups: updating active item and calling onGroupSelect');
      setActiveItem(item);
      setSelectedCategory('');
      onGroupSelect?.(item);
    } else {
      console.log('❌ PrecursorGroups: item unchanged, skipping');
    }
  };

  const handleCategorySelect = (category: string) => {
    console.log('🏷️ PrecursorGroups: selecting category:', category, 'current active:', activeItem, 'current selectedCategory:', selectedCategory);
    if (category !== activeItem) {
      console.log('✅ PrecursorGroups: updating active item to category and calling onGroupSelect');
      setActiveItem(category);
      console.log('🔧 PrecursorGroups: setting selectedCategory to:', category);
      setSelectedCategory(category);
      onGroupSelect?.(category);
    } else {
      console.log('❌ PrecursorGroups: category unchanged, skipping');
    }
  };

  const handleAllGroupsClick = () => {
    console.log('🌟 PrecursorGroups: selecting all groups, current active:', activeItem);
    if (activeItem !== '') {
      console.log('✅ PrecursorGroups: updating to all groups and calling onGroupSelect');
      setActiveItem('');
      setSelectedCategory('');
      onGroupSelect?.('');
    } else {
      console.log('❌ PrecursorGroups: already showing all groups, skipping');
    }
  };

  return (
    <Card style={{ borderColor: '#d9d9d9' }}>
      <Row gutter={[16, 16]} style={{ padding: '0px', margin: '0px' }}>
        <Col span={24} style={{padding: '0px'}}>
          <Typography.Title level={5}>Группы прекурсоров</Typography.Title>
        </Col>
        <Col span={24} style={{padding: '0px'}}>
          <Input
            placeholder="Поиск"
            prefix={<SearchOutlined />}
            style={{ width: "100%", height: '32px', borderRadius: '4px' }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
        <Col span={24} style={{padding: '0px'}}>
          <StyledListItem
            $isActive={activeItem === ''}
            $blueColor={token['blue-5']}
            onClick={handleAllGroupsClick}
            style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}
          >
            <span>Все прекурсоры</span>
          </StyledListItem>
          <Popover
            content={
              <SubmenuGroups 
                onGroupSelect={(category) => {
                  console.log('🎯 PrecursorGroups: received category from SubmenuGroups:', category);
                  handleCategorySelect(category);
                  setPopoverVisible(false);
                }}
                selectedGroup={activeItem}
              />
            }
            trigger="click"
            placement="rightTop"
            open={popoverVisible}
            onOpenChange={setPopoverVisible}
            overlayStyle={{ padding: 0 }}
            arrow={false}
          >
            <Button
              type="text"
              style={{ 
                width: "100%",
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                color: token['blue-5'],
                backgroundColor: '#f0f0f0',
                height: '40px'
              }}
            >
              <span>Группы 1-4</span>
              <RightOutlined style={{ color: token['blue-5'], fontSize: '10px', fontWeight: 'bold' }} />
            </Button>
          </Popover>
        </Col>

        <Col span={24} style={{ marginTop: '16px', padding: '0px' }}>
          <List
            loading={loading}
            dataSource={filteredData}
            renderItem={(item) => (
              <StyledListItem
                $isActive={activeItem === item}
                $blueColor={token['blue-5']}
                onClick={() => handleItemClick(item)}
              >
                {item}
              </StyledListItem>
            )}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default PrecursorGroups;
